import React, { useState, forwardRef } from 'react';

import './Section.css';

/**
 * A collapsible block with a heading.
 *
 * @param {object} props
 * @param {React.ReactNode} [props.icon] - Rendered before the heading, for what the section is about.
 * @param {React.ReactNode} props.heading - The heading, shown whether or not the section is open.
 * @param {React.ReactNode} [props.status] - Rendered beside the heading, for something that should
 *   be readable without opening the section.
 * @param {boolean} [props.muted] - Dims the heading, for a section describing something that is
 *   switched off. Only the heading: the section still opens, so its control stays as it is.
 * @param {boolean} [props.expandable=true] - False for a section with nothing to open onto: no
 *   toggle is drawn and the header does not respond to a click. The toggle's place is still held, so
 *   a list of sections keeps its headings aligned.
 * @param {React.ReactNode} props.children - The body, rendered only when the section can open.
 */
const _Section = ({ icon, heading, status, muted, expandable = true, children }, ref) => {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = e => {
    expandable && setExpanded(!expanded);
  };

  return (
    <div className={'Section' + (expanded && expandable ? ' expanded' : '')}>
      <div
        className={'section-header' + (expandable ? '' : ' not-expandable')}
        onClick={handleToggle}
      >
        <div className="section-toggle">
          {expandable && <div className="button-section-toggle"></div>}
        </div>
        {icon && <div className="section-icon">{icon}</div>}
        <div className={'section-title' + (muted ? ' muted' : '')}>{heading}</div>
        {status && <div className="section-status">{status}</div>}
      </div>
      {expandable && <div className="section-body">{children}</div>}
    </div>
  );
};
export const Section = forwardRef(_Section);
